/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import React, { useMemo, useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import SlickGrid from "@CubeComponents/html/SlickGrid";
// Imported via the explicit `/index` sub-path, not the bare directory specifier: the ambient
// `declare module "@CubeComponents/html/SlickGrid"` in types/slickGrid.d.ts shadows the bare form
// and exposes only a default, so the named type imports below resolve to nothing through it.
// (`ProjectTemplates.tsx` already routes around it the same way.) Type-only, so nothing is emitted.
import type { SlickgridReactInstanceX, ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid/index";
import type { GridOption } from "slickgrid-react";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { Formatters } from "slickgrid-react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import { InputAdornment } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import VisibleIcon from "@Assets/icons/svg/Visible.svg?react";
import NotVisibleIcon from "@Assets/icons/svg/NotVisible.svg?react";
import PartiallyVisibleIcon from "@Assets/icons/svg/PartiallyVisible.svg?react";

/**
 * A tree node as this component handles it. Callers bring their own row shape and pick the
 * identifier / parent / label keys through props, so every dynamic read goes through the index
 * signature; the state flags this component owns are named.
 */
type TreeNode = {
  isChecked?: boolean;
  isIndeterminate?: boolean;
  isCheckboxDisabled?: boolean;
  visibility?: number;
  __collapsed?: boolean;
  __hasChildren?: boolean;
  [key: string]: unknown;
};

/** A node id is whatever the caller's `identifierPropName` column holds. */
type TreeNodeId = unknown;

/** Payload of the imperative check-change entry point (also exposed on the grid handle). */
type CheckChangeArgs = {
  items: TreeNode | TreeNode[];
  checked: boolean;
  resetFirst?: boolean;
  fireCallback?: boolean;
};

/** Payload of the imperative visibility-change entry point. */
type VisibilityChangeArgs = {
  items: TreeNode | TreeNode[];
  visibility?: number;
  fireCallback?: boolean;
};

/** The dataView surface this component drives. */
type TreeDataView = {
  getItemById: (id: TreeNodeId) => TreeNode | undefined;
  getItem: (row: number) => TreeNode | undefined;
  getItems: () => TreeNode[];
  getFilteredItems: () => TreeNode[];
  updateItem: (id: TreeNodeId, item: TreeNode) => void;
  beginUpdate: () => void;
  endUpdate: () => void;
  getItemMetadata?: (rowIndex: number) => { cssClasses: string } | null;
};

/**
 * The slice of the grid's imperative handle this component drives. Stated structurally (the same
 * pattern as `useFileManagerLeftSideTree`'s `TreeGridHandle`) so the caller's row type stays its own
 * business; the two `handle*` members are bolted on by the setup effect below.
 */
type TreeGridHandle = {
  dataView?: TreeDataView;
  slickGrid?: { invalidate: () => void; render: () => void };
  filterService?: { disableFilterFunctionality: () => void };
  handleCheckChange?: (args: CheckChangeArgs) => void;
  handleVisibilityChange?: (args: VisibilityChangeArgs) => void;
};

type TreeGridRef = { current: TreeGridHandle | null };

/**
 * The PUBLIC ref surface. Refs are invariant, so a concrete `TreeGridHandle` here rejects every
 * caller: each of the nine consumers declares its own handle whose `dataView`/`slickGrid` methods are
 * parameterised by that screen's own row type (`LayerNode`, `TreeNode`, `CuiTreeNode`, `SheetTreeNode`,
 * …), and none of those is assignable to another. This states only the member NAMES the component
 * populates, leaving each member's shape to the caller; `gridRef` below re-asserts the concrete
 * `TreeGridHandle` for internal use, so nothing inside this file loses typing.
 */
type TreeViewForwardedHandle = {
  dataView?: object;
  slickGrid?: object;
  filterService?: object;
  refresh?: () => void;
  handleCheckChange?: (args: never) => void;
  handleVisibilityChange?: (args: never) => void;
};

/** A createRoot handle, as this component stores it for later unmounting. */
type CellRoot = { render: (node: React.ReactNode) => void; unmount: () => void };

/**
 * Props of the tree view. Callers pass any number of extra props straight through to the underlying
 * SlickGrid (the `...rest` spread), hence the index signature.
 */
type TreeViewProps = {
  treeData?: TreeNode[];
  onCheckChange?: (item: TreeNode | null, checked: boolean, allItems: TreeNode[]) => void;
  onVisibilityChange?: (item: TreeNode | null, visibility: number, allItems: TreeNode[]) => void;
  /** Built against the caller's own row type; only forwarded to the grid. */
  columns?: object[];
  gridOptions?: object;
  enableCheckboxSelection?: boolean;
  enableVisibility?: boolean;
  enableSearch?: boolean;
  searchPlaceholder?: TranslatedString;
  height?: string | number;
  width?: string | number;
  columnId?: string;
  identifierPropName?: string;
  multiple?: boolean;
  disabledParentSelection?: boolean;
  disableHeader?: boolean;
  parentPropName?: string;
  enableSelectAllCheckbox?: boolean;
  disableCheckBox?: boolean;
  initialSyncDataCallback?: (items: TreeNode[]) => void;
  onUpdateTreeDataCallback?: (items: TreeNode[]) => void;
  expandRowsOnCheckChange?: boolean;
  [key: string]: unknown;
};

// Self-managing header checkbox: manages its own React state so MUI SVG icons
// update correctly when checked/indeterminate change via imperative handle.
interface HeaderCheckboxHandle {
  updateState: (checked: boolean, indeterminate: boolean) => void;
}

const HeaderCheckbox = forwardRef<HeaderCheckboxHandle, { onToggle: () => void }>(({ onToggle }, ref) => {
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);

  useImperativeHandle(ref, () => ({
    updateState: (newChecked: boolean, newIndeterminate: boolean) => {
      setChecked(newChecked);
      setIndeterminate(newIndeterminate);
    },
  }));

  return <CheckBoxComponent checked={checked} indeterminate={indeterminate} onChange={onToggle} />;
});

HeaderCheckbox.displayName = "HeaderCheckbox";

const TreeViewComponent = React.forwardRef(
  (
    {
      treeData: initialTreeData,
      onCheckChange,
      onVisibilityChange,
      columns,
      gridOptions,
      enableCheckboxSelection = false,
      enableVisibility = false,
      enableSearch = false,
      searchPlaceholder,
      height = "25rem",
      width = "100%",
      columnId = "label",
      identifierPropName = "id",
      multiple,
      disabledParentSelection,
      disableHeader,
      parentPropName = "parentId",
      enableSelectAllCheckbox = true,
      disableCheckBox = false,
      initialSyncDataCallback,
      onUpdateTreeDataCallback,
      expandRowsOnCheckChange,
      ...rest
    }: TreeViewProps,
    ref: React.ForwardedRef<TreeViewForwardedHandle>,
  ) => {
    const { t } = useTranslation();
    const defaultSearchPlaceholder = searchPlaceholder || t("Search...");
    const innerRef = useRef<TreeGridHandle | null>(null);
    const gridRef = (ref || innerRef) as TreeGridRef;

    const [flatData, setFlatData] = useState<TreeNode[]>([]);
    const [searchValue, setSearchValue] = useState("");
    const [gridReady, setGridReady] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);

    // Refs for stable access in DOM event handlers (avoids stale closures)
    const handleCheckChangeRef = useRef<((args: CheckChangeArgs) => void) | null>(null);
    const handleVisibilityChangeRef = useRef<((args: VisibilityChangeArgs) => void) | null>(null);
    const toggleSelectAllRef = useRef<(() => void) | null>(null);
    const headerCheckboxRef = useRef<HeaderCheckboxHandle | null>(null);
    const identifierPropNameRef = useRef(identifierPropName);
    identifierPropNameRef.current = identifierPropName;

    // Track createRoot instances for cleanup
    const rootsRef = useRef<Map<TreeNodeId, CellRoot>>(new Map());
    const visibilityRootsRef = useRef<Map<TreeNodeId, CellRoot>>(new Map());

    useEffect(() => {
      const roots = rootsRef.current;
      const visibilityRoots = visibilityRootsRef.current;
      return () => {
        roots.forEach((root) => {
          try {
            root.unmount();
          } catch (e) {
            console.log(e);
            Promise.reject(e);
          }
        });
        roots.clear();
        visibilityRoots.forEach((root) => {
          try {
            root.unmount();
          } catch (e) {
            console.log(e);
            Promise.reject(e);
          }
        });
        visibilityRoots.clear();
      };
    }, []);

    // O(1) children lookup: parentId → [childId1, childId2, ...]
    const childIdsMapRef = useRef<Map<TreeNodeId, TreeNodeId[]>>(new Map());

    // Build child IDs map from flat data (tree structure is stable after init)
    const buildChildIdsMap = useCallback(
      (items: TreeNode[]) => {
        const map = new Map<TreeNodeId, TreeNodeId[]>();
        for (const item of items) {
          const pid = item[parentPropName];
          if (pid != null) {
            if (!map.has(pid)) map.set(pid, []);
            map.get(pid)!.push(item[identifierPropName]);
          }
        }
        childIdsMapRef.current = map;
      },
      [parentPropName, identifierPropName],
    );

    // Get children of a node - O(k) where k is number of children (was O(n))
    const getChildrenFromGrid = useCallback(
      (parentId: TreeNodeId): TreeNode[] => {
        const dv = gridRef.current?.dataView;
        if (!dv) return [];
        const childIds = childIdsMapRef.current.get(parentId) || [];
        return childIds.map((id: TreeNodeId) => dv.getItemById(id)).filter(Boolean) as TreeNode[];
      },
      [gridRef],
    );

    // Get all descendants iteratively - O(d) where d is total descendants
    const getDescendantsFromGrid = useCallback(
      (itemId: TreeNodeId): TreeNode[] => {
        const dv = gridRef.current?.dataView;
        if (!dv) return [];
        const descendants: TreeNode[] = [];
        const stack = [itemId];
        while (stack.length > 0) {
          const currentId = stack.pop();
          const childIds = childIdsMapRef.current.get(currentId) || [];
          for (const childId of childIds) {
            const child = dv.getItemById(childId);
            if (child) {
              descendants.push(child);
              stack.push(childId);
            }
          }
        }
        return descendants;
      },
      [gridRef],
    );

    // Get all ancestors using grid's dataView - O(d) where d is depth
    const getAncestorsFromGrid = useCallback(
      (item: TreeNode): TreeNode[] => {
        const dv = gridRef.current?.dataView;
        if (!dv) return [];

        const ancestors: TreeNode[] = [];
        let current: TreeNode | undefined = item;

        while (current?.[parentPropName] != null) {
          const parent = dv.getItemById(current[parentPropName]);
          if (parent) {
            ancestors.push(parent);
            current = parent;
          } else {
            break;
          }
        }

        return ancestors;
      },
      [parentPropName, gridRef],
    );

    // Shared helper: compute tree depth (distance from root) - O(d) where d is depth
    const getTreeDepth = useCallback(
      (item: TreeNode): number => {
        const dv = gridRef.current?.dataView;
        if (!dv) return 0;
        let depth = 0;
        let current: TreeNode | undefined = item;
        while (current?.[parentPropName] != null) {
          depth++;
          current = dv.getItemById(current[parentPropName]);
        }
        return depth;
      },
      [parentPropName, gridRef],
    );

    // const // expandGridRow = (item: any) => {
    //   if(item && expandRowsOnCheckChange) {
    //     gridRef.current.treeDataService.dynamicallyToggleItemState([{...item, itemId:  item.id}])
    //   }
    // }

    // Update parent state using grid's dataView - single pass for counting
    const updateParentStateFromGrid = useCallback(
      (parentId: TreeNodeId) => {
        const dv = gridRef.current?.dataView;
        if (!dv) return;

        const parent = dv.getItemById(parentId);
        if (!parent) return;

        const children = getChildrenFromGrid(parentId);
        if (children.length === 0) return;

        // Single pass: count checked and indeterminate
        let checkedCount = 0;
        let hasIndeterminate = false;
        for (const child of children) {
          if (child.isChecked) checkedCount++;
          if (child.isIndeterminate) hasIndeterminate = true;
        }

        const isChecked = checkedCount === children.length;
        const isIndeterminate = !isChecked && (checkedCount > 0 || hasIndeterminate);

        if (parent.isChecked !== isChecked || parent.isIndeterminate !== isIndeterminate) {
          dv.updateItem(parentId, {
            ...parent,
            isChecked,
            isIndeterminate,
            __collapsed: expandRowsOnCheckChange ? false : parent.__collapsed, // expand if needed
          });
          // // expandGridRow(parent)
        }
      },
      [getChildrenFromGrid, expandRowsOnCheckChange, gridRef],
    );

    // Update header checkbox state to reflect current data via imperative handle
    const updateHeaderCheckbox = useCallback(() => {
      const dv = gridRef.current?.dataView;
      const handle = headerCheckboxRef.current;
      if (!dv || !handle) return;

      const allItems = dv.getItems() || [];
      if (allItems.length === 0) {
        handle.updateState(false, false);
        return;
      }

      const checkedCount = allItems.filter((c: TreeNode) => c.isChecked).length;
      const totalCount = allItems.length;
      handle.updateState(checkedCount === totalCount, checkedCount > 0 && checkedCount < totalCount);
    }, [gridRef]);

    // Helper: Build maps for tree relationships
    const buildTreeMaps = useCallback(
      (data: TreeNode[]) => {
        const itemById = new Map<TreeNodeId, TreeNode>();
        const childrenByParentId = new Map<TreeNodeId, TreeNode[]>();

        for (const item of data) {
          itemById.set(item[identifierPropName], item);
          const pid = item[parentPropName];
          if (pid != null) {
            childrenByParentId.set(pid, [...(childrenByParentId.get(pid) || []), item]);
          }
        }
        return { itemById, childrenByParentId };
      },
      [identifierPropName, parentPropName],
    );

    // Helper: Propagate checked state down to all descendants
    const propagateCheckedDown = useCallback(
      (itemId: TreeNodeId, childrenByParentId: Map<TreeNodeId, TreeNode[]>) => {
        const children = childrenByParentId.get(itemId) || [];
        for (const child of children) {
          child.isChecked = true;
          child.isIndeterminate = false;
          propagateCheckedDown(child[identifierPropName], childrenByParentId);
        }
      },
      [identifierPropName],
    );

    // Helper: Propagate hidden state down to all descendants
    const propagateHiddenDown = useCallback(
      (itemId: TreeNodeId, childrenByParentId: Map<TreeNodeId, TreeNode[]>) => {
        const children = childrenByParentId.get(itemId) || [];
        for (const child of children) {
          child.visibility = 0;
          propagateHiddenDown(child[identifierPropName], childrenByParentId);
        }
      },
      [identifierPropName],
    );

    // Helper: Compute depth for each node (iterative to avoid stack overflow on deep trees)
    const computeDepths = useCallback(
      (data: TreeNode[], itemById: Map<TreeNodeId, TreeNode>) => {
        const depthById = new Map<TreeNodeId, number>();

        const getDepth = (startId: TreeNodeId): number => {
          const chain: TreeNodeId[] = [];
          const seen = new Set<TreeNodeId>();
          let cur: TreeNodeId = startId;
          let parentDepth = -1; // depth of the node just above the last chain entry

          while (cur != null) {
            if (depthById.has(cur)) {
              parentDepth = depthById.get(cur)!;
              break;
            }
            if (seen.has(cur)) break; // cycle
            seen.add(cur);
            chain.push(cur);
            const item = itemById.get(cur);
            cur = item?.[parentPropName] ?? null;
          }

          const rootDepth = parentDepth === -1 ? 0 : parentDepth + 1;
          for (let i = chain.length - 1, d = rootDepth; i >= 0; i--, d++) {
            depthById.set(chain[i], d);
          }
          return depthById.get(startId) ?? 0;
        };

        for (const item of data) {
          getDepth(item[identifierPropName]);
        }
        return depthById;
      },
      [identifierPropName, parentPropName],
    );

    // Helper: Derive parent state from children
    const deriveParentState = (parent: TreeNode, children: TreeNode[]) => {
      let checkedCount = 0;
      let hasIndeterminate = false;
      let visibleCount = 0;
      let hasPartial = false;

      for (const child of children) {
        if (child.isChecked) checkedCount++;
        if (child.isIndeterminate) hasIndeterminate = true;
        if (child.visibility === 1) visibleCount++;
        else if (child.visibility === 2) hasPartial = true;
      }

      parent.isChecked = checkedCount === children.length;
      parent.isIndeterminate = !parent.isChecked && (checkedCount > 0 || hasIndeterminate);

      if (visibleCount === children.length) {
        parent.visibility = 1;
      } else if (visibleCount > 0 || hasPartial) {
        parent.visibility = 2;
      } else {
        parent.visibility = 0;
      }
    };

    // Initialize flat data - only runs once on mount, uses temporary maps
    const initializeFlatData = useCallback(
      (data: TreeNode[]) => {
        if (!data || data.length === 0) return [];

        // Clone and set defaults
        const clonedData = data.map((item) => ({
          ...item,
          isChecked: item.isChecked || false,
          isIndeterminate: item.isIndeterminate || false,
          visibility: item.visibility ?? 1,
        }));

        const { itemById, childrenByParentId } = buildTreeMaps(clonedData);

        // Step 1: Propagate DOWN - checked and hidden states
        for (const item of clonedData) {
          // The object spread that builds `clonedData` produces a fresh literal type, which drops
          // `TreeNode`'s string index signature; `identifierPropName` is a caller-supplied column
          // name, so it is re-asserted here. Both callees take `TreeNodeId` (= `unknown`).
          if (item.isChecked)
            propagateCheckedDown(
              (item as typeof item & Record<string, unknown>)[identifierPropName],
              childrenByParentId,
            );
          if (item.visibility === 0)
            propagateHiddenDown(
              (item as typeof item & Record<string, unknown>)[identifierPropName],
              childrenByParentId,
            );
        }

        // Step 2: Compute depths and collect parent nodes sorted deepest-first
        const depthById = computeDepths(clonedData, itemById);
        const parentNodes = (
          [...childrenByParentId.keys()].map((pid) => itemById.get(pid)).filter(Boolean) as TreeNode[]
        ).sort((a, b) => (depthById.get(b[identifierPropName]) ?? 0) - (depthById.get(a[identifierPropName]) ?? 0));

        // Step 3: Propagate UP - derive parent state from children
        for (const parent of parentNodes) {
          const children = childrenByParentId.get(parent[identifierPropName]) || [];
          deriveParentState(parent, children);
        }

        buildChildIdsMap(clonedData);
        return clonedData;
      },
      [identifierPropName, buildTreeMaps, propagateCheckedDown, propagateHiddenDown, computeDepths, buildChildIdsMap],
    );

    // Helper: Reset all checkboxes in the grid
    const resetAllCheckboxes = useCallback(
      (dv: TreeDataView) => {
        const allItems = dv.getItems();
        for (const item of allItems) {
          if (item.isChecked || item.isIndeterminate) {
            dv.updateItem(item[identifierPropName], {
              ...item,
              isChecked: false,
              isIndeterminate: false,
              __collapsed: expandRowsOnCheckChange ? false : item.__collapsed,
            });
          }
        }
      },
      [identifierPropName, expandRowsOnCheckChange],
    );

    // Helper: Update descendants checked state
    const updateDescendantsChecked = useCallback(
      (dv: TreeDataView, itemId: TreeNodeId, checked: boolean) => {
        const descendants = getDescendantsFromGrid(itemId);
        for (const descendant of descendants) {
          const freshDescendant = dv.getItemById(descendant[identifierPropName]);
          if (freshDescendant) {
            dv.updateItem(freshDescendant[identifierPropName], {
              ...freshDescendant,
              isChecked: checked,
              isIndeterminate: false,
              __collapsed: expandRowsOnCheckChange ? false : freshDescendant.__collapsed, // expand if needed
            });
            // expandGridRow(freshDescendant)
          }
        }
      },
      [getDescendantsFromGrid, identifierPropName, expandRowsOnCheckChange],
    );

    // Helper: Collect ancestors with their tree depth
    const collectAncestorDepths = useCallback(
      (item: TreeNode, ancestorDepthMap: Map<TreeNodeId, number>) => {
        const ancestors = getAncestorsFromGrid(item);
        for (const ancestor of ancestors) {
          const ancestorId = ancestor[identifierPropName];
          if (!ancestorDepthMap.has(ancestorId)) {
            ancestorDepthMap.set(ancestorId, getTreeDepth(ancestor));
          }
        }
      },
      [getAncestorsFromGrid, identifierPropName, getTreeDepth],
    );

    // Helper: Update ancestors deepest-first
    const updateAncestorsDeepestFirst = useCallback(
      (ancestorDepthMap: Map<TreeNodeId, number>) => {
        const sortedAncestors = Array.from(ancestorDepthMap.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([id]) => id);

        for (const ancestorId of sortedAncestors) {
          updateParentStateFromGrid(ancestorId);
        }
      },
      [updateParentStateFromGrid],
    );

    // Handle checkbox change using grid methods - supports single item or array of items
    const handleCheckChange = useCallback(
      ({ items: itemOrItems, checked, resetFirst = false, fireCallback = true }: CheckChangeArgs) => {
        const dv = gridRef.current?.dataView;
        if (!dv) return;

        // Normalize to array
        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];

        dv.beginUpdate();
        if (resetFirst) {
          resetAllCheckboxes(dv);
        }
        dv.endUpdate();

        if (items.length === 0) return;
        dv.beginUpdate();

        const ancestorDepthMap = new Map<TreeNodeId, number>();

        for (const item of items) {
          if (!item) continue;

          const freshItem = dv.getItemById(item[identifierPropName]);
          if (!freshItem) continue;

          // Update all descendants, then the item itself
          updateDescendantsChecked(dv, freshItem[identifierPropName], checked);
          dv.updateItem(freshItem[identifierPropName], {
            ...freshItem,
            isChecked: checked,
            isIndeterminate: false,
            __collapsed: expandRowsOnCheckChange ? false : freshItem.__collapsed, // expand if needed
          });

          // expandGridRow(freshItem)

          collectAncestorDepths(freshItem, ancestorDepthMap);
        }

        // Update ancestors deepest-first
        updateAncestorsDeepestFirst(ancestorDepthMap);

        dv.endUpdate();

        updateHeaderCheckbox();

        if (onCheckChange && fireCallback) {
          onCheckChange(items.length === 1 ? items[0]! : null, checked, dv.getItems());
        }

        onUpdateTreeDataCallback?.(dv.getItems());
        requestAnimationFrame(() => updateHeaderCheckbox());
      },

      [
        resetAllCheckboxes,
        updateDescendantsChecked,
        collectAncestorDepths,
        updateAncestorsDeepestFirst,
        identifierPropName,
        onCheckChange,
        updateHeaderCheckbox,
      ],
    );

    // Keep ref updated for stable access in plain DOM event handlers
    handleCheckChangeRef.current = handleCheckChange;

    const checkBoxComponentTemplate = useCallback(
      (params: TreeNode) => {
        const dv = gridRef.current?.dataView;
        const idProp = identifierPropNameRef.current;
        const itemId = params[idProp];
        const item = dv?.getItemById(itemId);
        if (!item) return document.createElement("div");

        // Cleanup previous root for this item
        const prevRoot = rootsRef.current.get(itemId);
        if (prevRoot) {
          try {
            prevRoot.unmount();
          } catch (e) {
            console.log(e);
            Promise.reject(e);
          }
        }

        const container = document.createElement("div");
        container.style.width = "100%";
        const root = createRoot(container);
        rootsRef.current.set(itemId, root);

        root.render(
          <CheckBoxComponent
            disabled={disableCheckBox || !!item.isCheckboxDisabled}
            checked={item.isChecked}
            indeterminate={item.isIndeterminate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const freshItem = gridRef.current?.dataView?.getItemById(itemId);
              if (freshItem) {
                handleCheckChangeRef.current?.({ items: freshItem, checked: e.target.checked });
              }
            }}
          />,
        );
        return container;
      },
      [disableCheckBox, gridRef], // Stable — uses refs for all mutable values
    );

    // Update parent visibility state based on children - single pass for counting
    const updateParentVisibilityFromGrid = useCallback(
      (parentId: TreeNodeId) => {
        const dv = gridRef.current?.dataView;
        if (!dv) return;

        const parent = dv.getItemById(parentId);
        if (!parent) return;

        const children = getChildrenFromGrid(parentId);
        if (children.length === 0) return;

        // Single pass: count visible and check for partial
        let visibleCount = 0;
        let hasPartial = false;
        for (const child of children) {
          if (child.visibility === 1) visibleCount++;
          else if (child.visibility === 2) hasPartial = true;
        }

        let visibility = 0; // hidden
        if (visibleCount === children.length) {
          visibility = 1; // visible
        } else if (visibleCount > 0 || hasPartial) {
          visibility = 2; // partial
        }

        if (parent.visibility !== visibility) {
          dv.updateItem(parentId, {
            ...parent,
            visibility,
            __collapsed: expandRowsOnCheckChange ? false : parent.__collapsed, // expand if needed
          });
        }
      },
      [getChildrenFromGrid, expandRowsOnCheckChange, gridRef],
    );

    // Helper: Determine new visibility (from arg or toggle)
    const determineNewVisibility = useCallback(
      (dv: TreeDataView, items: TreeNode[], visibilityArg: number | undefined): number | null => {
        if (visibilityArg !== undefined) return visibilityArg;

        const firstItem = items.find(Boolean);
        if (!firstItem) return null;

        const freshFirst = dv.getItemById(firstItem[identifierPropName]);
        if (!freshFirst) return null;

        return freshFirst.visibility === 0 ? 1 : 0;
      },
      [identifierPropName],
    );

    // Helper: Update descendants visibility
    const updateDescendantsVisibility = useCallback(
      (dv: TreeDataView, itemId: TreeNodeId, visibility: number) => {
        const descendants = getDescendantsFromGrid(itemId);
        for (const descendant of descendants) {
          const freshDescendant = dv.getItemById(descendant[identifierPropName]);
          if (freshDescendant) {
            dv.updateItem(freshDescendant[identifierPropName], {
              ...freshDescendant,
              visibility,
              __collapsed: expandRowsOnCheckChange ? false : freshDescendant.__collapsed, // expand if needed
            });
            // expandGridRow(freshDescendant)
          }
        }
      },
      [getDescendantsFromGrid, identifierPropName, expandRowsOnCheckChange],
    );

    // Helper: Update ancestors visibility deepest-first
    const updateAncestorsVisibilityDeepestFirst = useCallback(
      (ancestorDepthMap: Map<TreeNodeId, number>) => {
        const sortedAncestors = Array.from(ancestorDepthMap.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([id]) => id);

        for (const ancestorId of sortedAncestors) {
          updateParentVisibilityFromGrid(ancestorId);
        }
      },
      [updateParentVisibilityFromGrid],
    );

    // Handle visibility change with parent/child propagation - supports single item or array
    const handleVisibilityChange = useCallback(
      ({ items: itemOrItems, visibility: visibilityArg, fireCallback = true }: VisibilityChangeArgs) => {
        const dv = gridRef.current?.dataView;
        if (!dv) return;

        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
        if (items.length === 0) return;

        const newVisibility = determineNewVisibility(dv, items, visibilityArg);
        if (newVisibility === null) return;

        dv.beginUpdate();

        const ancestorDepthMap = new Map<TreeNodeId, number>();

        for (const item of items) {
          if (!item) continue;

          const freshItem = dv.getItemById(item[identifierPropName]);
          if (!freshItem) continue;

          updateDescendantsVisibility(dv, freshItem[identifierPropName], newVisibility);
          dv.updateItem(freshItem[identifierPropName], {
            ...freshItem,
            visibility: newVisibility,
            __collapsed: expandRowsOnCheckChange ? false : freshItem.__collapsed, // expand if needed
          });

          collectAncestorDepths(freshItem, ancestorDepthMap);
        }

        updateAncestorsVisibilityDeepestFirst(ancestorDepthMap);

        dv.endUpdate();

        if (onVisibilityChange && fireCallback) {
          onVisibilityChange(items.length === 1 ? items[0]! : null, newVisibility, dv.getItems());
        }
        onUpdateTreeDataCallback?.(dv.getItems());
      },

      [
        determineNewVisibility,
        updateDescendantsVisibility,
        collectAncestorDepths,
        updateAncestorsVisibilityDeepestFirst,
        identifierPropName,
        onVisibilityChange,
      ],
    );

    // Keep ref updated for stable access in plain DOM event handlers
    handleVisibilityChangeRef.current = handleVisibilityChange;

    const visibilityComponentTemplate = useCallback(
      (params: TreeNode) => {
        const dv = gridRef.current?.dataView;
        const idProp = identifierPropNameRef.current;
        const itemId = params[idProp];
        const item = dv?.getItemById(itemId);
        if (!item) return document.createElement("div");

        // Cleanup previous root for this item
        const prevRoot = visibilityRootsRef.current.get(itemId);
        if (prevRoot) {
          try {
            prevRoot.unmount();
          } catch (e) {
            console.log(e);
            Promise.reject(e);
          }
        }

        const container = document.createElement("div");
        container.style.width = "100%";
        const root = createRoot(container);
        visibilityRootsRef.current.set(itemId, root);

        let VisibilityIcon = VisibleIcon;
        if (item.visibility === 0) VisibilityIcon = NotVisibleIcon;
        if (item.visibility === 2) VisibilityIcon = PartiallyVisibleIcon;

        root.render(
          <button
            className="visibility-cell flex items-center justify-center w-full h-full cursor-pointer select-none"
            onClick={() => {
              const freshItem = gridRef.current?.dataView?.getItemById(itemId);
              if (freshItem) {
                handleVisibilityChangeRef.current?.({ items: freshItem });
              }
            }}
          >
            <div className="flex items-center justify-center w-4 h-4">
              <VisibilityIcon />
            </div>
          </button>,
        );
        return container;
      },
      [gridRef], // Stable — uses refs for all mutable values
    );

    const toggleSelectAllCheckbox = useCallback(() => {
      const dv = gridRef.current?.dataView;
      if (!dv) return;

      const visibleItems = dv.getFilteredItems() || [];
      const checkedCount = visibleItems.filter((c: TreeNode) => c.isChecked).length;
      const allChecked = checkedCount === visibleItems.length && visibleItems.length > 0;
      const checked = !allChecked;

      dv.beginUpdate();

      for (const item of visibleItems) {
        dv.updateItem(item[identifierPropName], {
          ...item,
          isChecked: checked,
          isIndeterminate: false,
          __collapsed: expandRowsOnCheckChange ? false : item.__collapsed, // expand if needed
        });
      }

      // Recalculate parent states
      const parentsToUpdate = new Set<TreeNodeId>();
      for (const item of visibleItems) {
        if (item[parentPropName] != null) {
          const ancestors = getAncestorsFromGrid(item);
          for (const ancestor of ancestors) {
            parentsToUpdate.add(ancestor[identifierPropName]);
          }
        }
      }

      for (const parentId of parentsToUpdate) {
        updateParentStateFromGrid(parentId);
      }

      dv.endUpdate();
      // endUpdate() fires onRowsChanged which re-renders only affected rows.
      // No invalidate() needed — avoids full grid re-render and preserves tree expand/collapse state.

      // Sync header checkbox with current state
      updateHeaderCheckbox();

      if (onCheckChange) {
        onCheckChange(null, checked, dv.getItems());
      }

      // Safety: re-sync header after any React re-render triggered by onCheckChange
      requestAnimationFrame(() => updateHeaderCheckbox());
    }, [
      getAncestorsFromGrid,
      updateParentStateFromGrid,
      identifierPropName,
      parentPropName,
      onCheckChange,
      updateHeaderCheckbox,
      expandRowsOnCheckChange,
      gridRef,
    ]);

    // Keep ref updated for stable access in header DOM event handler
    toggleSelectAllRef.current = toggleSelectAllCheckbox;

    // Header checkbox as JSX (slickgrid-react renders headers via React's createRoot).
    // The wrapper calls headerTemplate({ column, onClick }) — we ignore the args.
    // Uses HeaderCheckbox component with imperative handle for proper MUI icon updates.
    const headerTemplate = useCallback((_args?: unknown) => {
      return (
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <HeaderCheckbox ref={headerCheckboxRef} onToggle={() => toggleSelectAllRef.current?.()} />
        </div>
      );
    }, []);

    // Memoize column definitions to prevent slickgrid-react from re-mounting
    // headers on every React re-render (which would reset header checkbox state)
    const cols = useMemo(() => {
      const baseCols: object[] = columns || [
        {
          id: columnId,
          name: "Name",
          field: columnId,
          sortable: true,
          resizable: true,
          formatter: Formatters.tree,
        },
      ];

      const resultCols: object[] = [];
      // Add checkbox column if enabled
      if (enableCheckboxSelection) {
        resultCols.push({
          id: "checkbox",
          name: "",
          field: columnId,
          sortable: false,
          resizable: false,
          // Slickgrid calls a formatter as (row, cell, value, columnDef, dataContext); the parameter
          // names here predate that and are kept verbatim - `params` is the ROW.
          formatter: (data: number, value: number, a: unknown, b: unknown, params: TreeNode) =>
            checkBoxComponentTemplate(params),
          maxWidth: 50,
          ...(enableSelectAllCheckbox && { headerTemplate: headerTemplate }),
        });
      }

      // Add visibility column if enabled
      if (enableVisibility) {
        resultCols.push({
          id: "visibility",
          name: "",
          field: columnId,
          sortable: false,
          resizable: false,
          formatter: (data: number, value: number, a: unknown, b: unknown, params: TreeNode) =>
            visibilityComponentTemplate(params),
          maxWidth: 36,
          selectable: false,
          focusable: false,
        });
      }

      return [...resultCols, ...baseCols];
      // gridReady is included so cols gets a new reference when the grid becomes ready,
      // which triggers the SlickGrid wrapper's header template effect (it depends on
      // props.columnDefinitions and has an early return when grid isn't ready).
    }, [
      columns,
      columnId,
      enableCheckboxSelection,
      enableVisibility,
      enableSelectAllCheckbox,
      checkBoxComponentTemplate,
      visibilityComponentTemplate,
      headerTemplate,
      gridReady,
    ]);

    const defaultGridOptions = useMemo(
      () => ({
        enableCheckboxSelector: false,
        enableRowSelection: true,
        enableCellNavigation: true,
        enableTreeData: true,
        enableSorting: true,
        forceFitColumns: true,
        multiSelect: multiple,
        treeDataOptions: {
          columnId: columnId,
          parentPropName: parentPropName,
          identifierPropName: identifierPropName,
        },
      }),
      [columnId, parentPropName, identifierPropName, multiple],
    );

    const escapeRegExp = (str: string) => str.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

    // Helper: Add all ancestors to included set
    const addAncestors = useCallback(
      (id: TreeNodeId, itemById: Map<TreeNodeId, TreeNode>, includedIds: Set<TreeNodeId>) => {
        let current: TreeNode | undefined = itemById.get(id);
        while (current?.[parentPropName] != null) {
          const parent = itemById.get(current[parentPropName]);
          if (!parent) break;
          includedIds.add(parent[identifierPropName]);
          current = parent;
        }
      },
      [identifierPropName, parentPropName],
    );

    // Helper: Add all descendants to included set
    const addDescendants = useCallback(
      (itemId: TreeNodeId, childrenByParentId: Map<TreeNodeId, TreeNode[]>, includedIds: Set<TreeNodeId>) => {
        const children = childrenByParentId.get(itemId) || [];
        for (const child of children) {
          includedIds.add(child[identifierPropName]);
          addDescendants(child[identifierPropName], childrenByParentId, includedIds);
        }
      },
      [identifierPropName],
    );

    // Filter using grid's dataView when available, otherwise use flatData
    const filteredFlatData = useMemo(() => {
      if (!enableSearch || !searchValue) {
        return flatData;
      }

      const safeVal = escapeRegExp(searchValue);
      const regex = new RegExp(safeVal, "i");

      const matchingIds = new Set<TreeNodeId>();
      const { itemById, childrenByParentId } = buildTreeMaps(flatData);

      for (const item of flatData) {
        // The label column is caller-configurable, so it is read through the index signature.
        if (regex.test((item[columnId] as string) || "")) {
          matchingIds.add(item[identifierPropName]);
        }
      }

      // Include ancestors and descendants of matching items
      const includedIds = new Set<TreeNodeId>(matchingIds);
      for (const id of matchingIds) {
        addAncestors(id, itemById, includedIds);
        addDescendants(id, childrenByParentId, includedIds);
      }

      return flatData.filter((item) => includedIds.has(item[identifierPropName]));
    }, [
      flatData,
      searchValue,
      enableSearch,
      columnId,
      identifierPropName,
      buildTreeMaps,
      addAncestors,
      addDescendants,
    ]);

    // Initialize data - use requestAnimationFrame to ensure loader shows first
    useEffect(() => {
      if (!initialTreeData || initialTreeData.length === 0) {
        setFlatData([]);
        setIsInitializing(false);
        return;
      }

      setIsInitializing(true);
      let cancelled = false;

      // Allow the loader to render before processing large datasets
      const frameId = requestAnimationFrame(() => {
        if (cancelled) return;
        const initialized = initializeFlatData(initialTreeData);
        setFlatData(initialized);
        setIsInitializing(false);
        initialSyncDataCallback?.(initialized);
      });

      // Cleanup: cancel pending frame if component unmounts or data changes
      return () => {
        cancelled = true;
        cancelAnimationFrame(frameId);
      };
    }, [initialTreeData, initializeFlatData]);

    // Setup grid after ready
    useEffect(() => {
      if (!gridReady) return;

      if (gridRef.current) {
        gridRef.current.handleCheckChange = (args: CheckChangeArgs) => handleCheckChangeRef.current?.(args);
        gridRef.current.handleVisibilityChange = (args: VisibilityChangeArgs) =>
          handleVisibilityChangeRef.current?.(args);
      }

      gridRef.current?.filterService?.disableFilterFunctionality();

      if (gridRef.current?.dataView && disabledParentSelection) {
        const dv = gridRef.current.dataView;
        dv.getItemMetadata = (rowIndex: number) => {
          const item = dv.getItem(rowIndex);
          if (item?.__hasChildren) {
            return { cssClasses: "slick-disable-selection-row" };
          }
          return null;
        };
        gridRef.current.slickGrid?.invalidate();
        gridRef.current.slickGrid?.render();
      }

      // Set initial header checkbox state after grid is ready.
      // Header is rendered via createRoot.render() (async), so defer to ensure it's mounted.
      updateHeaderCheckbox();
      requestAnimationFrame(() => updateHeaderCheckbox());
    }, [gridReady, disabledParentSelection, updateHeaderCheckbox, gridRef]);

    const handleGridReady = useCallback((ready: boolean) => {
      setGridReady(ready);
    }, []);

    return (
      <div className="relative flex flex-col overflow-hidden" style={{ width, height }}>
        {enableSearch && (
          <div className="p-4 shrink-0">
            <TextFieldComponent
              type="text"
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
              placeholder={defaultSearchPlaceholder}
              className="w-full py-1"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconComponent name="SearchIcon" width="1.5rem" height="1.5rem" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        )}

        {isInitializing && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-white/80"
            style={{ top: enableSearch ? "4.5rem" : 0 }}
          >
            <CircularProgress size={32} />
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0 }}>
          <SlickGrid
            gridOptions={(gridOptions || defaultGridOptions) as Partial<GridOption>}
            // Columns arrive from the caller already built against its own row type (or are the two
            // synthesised checkbox/visibility columns); brand them here, at the grid boundary.
            columnDefinitions={cols as ValidatedColumnDefinition<TreeNode>[]}
            dataset={filteredFlatData}
            // The forwarded ref is the SlickGrid instance itself; this component only names the slice
            // of it that it drives (see TreeGridHandle).
            ref={gridRef as typeof gridRef & React.RefObject<SlickgridReactInstanceX<TreeNode> | null>}
            disableHeader={disableHeader}
            disabledParentSelection={disabledParentSelection}
            setGridReady={handleGridReady}
            enableFiltering
            showPageCount={false}
            {...rest}
          />
        </div>
      </div>
    );
  },
);

TreeViewComponent.displayName = "TreeViewComponent";

export default TreeViewComponent;
